"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { ProfileScreen } from "@/features/profile/ProfileScreen";
import { usePlayer } from "@/contexts/PlayerContext";
import { getMe, updateMe } from "@/lib/api/endpoints";
import { ApiError } from "@/lib/api/api";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth.store";
import { useMatchStatsSummary, useRecentMatches } from "@/lib/queries/stats.queries";
import { useRankedProfile, useUserRanks } from "@/lib/queries/ranked.queries";
import { queryKeys } from "@/lib/queries/queryKeys";
import { useLocale } from "@/contexts/LocaleContext";
import { LOCALES, type Locale } from "@/lib/i18n/locale-config";
import { toProfileRecentMatch } from "@/features/profile/ProfileWeb";
import { MAX_MATCHES_COUNT } from "@/lib/constants/matches";
import { useEffect } from "react";
import { useMyAchievements, usePublicProfile } from "@/lib/queries/users.queries";
import { decodeAvatarCustomization } from "@/lib/avatars";
import { trackNicknameChanged, trackFavoriteClubChanged } from "@/lib/analytics/game-events";
import {
  formatCooldownDate,
  getNicknameCooldown,
  getNicknameRejection,
  type NicknameRejection,
} from "@/lib/api/nicknameErrors";

export default function ProfilePage() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { player, updateStats } = usePlayer();
  const authUser = useAuthStore((state) => state.user);
  const setAuthenticated = useAuthStore((state) => state.setAuthenticated);
  const {
    data: recentMatches = [],
    isLoading: recentMatchesLoading,
    error: recentMatchesError,
  } = useRecentMatches(MAX_MATCHES_COUNT);
  const { data: matchStatsSummary = null } = useMatchStatsSummary();
  const { data: rankedProfile, isLoading: rankedProfileLoading } = useRankedProfile();
  const { data: userRanks } = useUserRanks();
  const { data: achievements = [] } = useMyAchievements();
  // Own previous nicknames come from the public profile so self and visitor
  // views render from the same server-filtered list.
  const { data: ownPublicProfile } = usePublicProfile(authUser?.id);

  const { setLocale, t, locale } = useLocale();
  const [isUpdating, setIsUpdating] = useState(false);
  const purchaseStatus = searchParams.get("purchase");

  useEffect(() => {
    if (!purchaseStatus) return;
    if (purchaseStatus === "success") {
      toast.success(t("profile.purchaseCompleted"));
      void queryClient.invalidateQueries({ queryKey: queryKeys.store.inventory() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.store.wallet() });
    }
    if (purchaseStatus === "cancelled") {
      toast.message(t("profile.purchaseCancelled"));
    }
    // Clear the query param so the toast doesn't re-fire on remount/navigation
    const params = new URLSearchParams(searchParams.toString());
    params.delete("purchase");
    const cleaned = params.toString();
    router.replace(cleaned ? `?${cleaned}` : window.location.pathname, { scroll: false });
  }, [purchaseStatus, queryClient, router, searchParams, t]);

  // Every rejection used to collapse into one "failed to update" toast, leaving
  // the user unsure whether to pick another name, wait, or retry.
  const describeNicknameRejection = (
    rejection: NicknameRejection | null,
    error: unknown,
  ): string => {
    switch (rejection) {
      case "taken":
        return t("profile.nicknameTaken");
      case "recently_released":
        return t("profile.nicknameRecentlyReleased");
      case "prohibited_content":
        return t("profile.nicknameProhibited");
      case "empty":
        return t("profile.nicknameEmpty");
      case "cooldown": {
        const nextAvailableAt = getNicknameCooldown(error)?.nextAvailableAt;
        return nextAvailableAt
          ? t("profileScreen.nicknameCooldownUntil", {
              date: formatCooldownDate(new Date(nextAvailableAt), locale),
            })
          : t("profileScreen.nicknameNoChangesLeft");
      }
      default:
        return t("profile.pleaseTryAgain");
    }
  };

  const handleNameChange = async (name: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      const updated = await updateMe({ nickname: name });
      updateStats({ username: name });
      // Take the whole response, not a spread of the stale user: it carries the
      // decremented quota, so the profile locks itself on the last change
      // instead of still offering one.
      setAuthenticated(updated);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.users.publicProfile(updated.id),
      });
      try {
        trackNicknameChanged();
      } catch (analyticsError) {
        console.error('Analytics tracking failed', analyticsError);
      }
      toast.success(t("profile.nicknameUpdated"));
    } catch (error) {
      const rejection = getNicknameRejection(error);

      if (rejection === "cooldown") {
        // Pull the authoritative quota so the profile switches to the locked
        // state instead of still offering an edit the server keeps rejecting.
        void getMe()
          .then((fresh) => setAuthenticated(fresh))
          .catch(() => undefined);
      }

      toast.error(t("profile.nicknameUpdateFailed"), {
        description: describeNicknameRejection(rejection, error),
      });
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAvatarChange = async (avatarUrl: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      const avatarCustomization = decodeAvatarCustomization(avatarUrl);
      const updated = avatarCustomization
        ? await updateMe({ avatar_url: null, avatar_customization: avatarCustomization })
        : await updateMe({ avatar_url: null, avatar_customization: null });
      updateStats({ avatarCustomization: avatarCustomization ?? undefined });
      if (authUser) {
        setAuthenticated({
          ...authUser,
          avatar_url: updated.avatar_url,
          avatar_customization: updated.avatar_customization ?? avatarCustomization,
        });
      }
      toast.success(t("profile.avatarUpdated"));
    } catch (error) {
      toast.error(t("profile.avatarUpdateFailed"), {
        description: error instanceof Error ? error.message : t("profile.pleaseTryAgain"),
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClubChange = async (club: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      const updated = await updateMe({ favorite_club: club });
      if (authUser) {
        setAuthenticated({ ...authUser, favorite_club: updated.favorite_club });
      }
      try {
        trackFavoriteClubChanged(club);
      } catch (analyticsError) {
        console.error('Analytics tracking failed', analyticsError);
      }
      // Club boards and standings follow the new club.
      void queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard.all });
      toast.success(t("profile.favoriteClubUpdated"));
    } catch (error) {
      const retryAt = clubCooldownRetryAt(error);
      if (retryAt) {
        toast.error(t("profile.favoriteClubCooldown", {
          date: retryAt.toLocaleDateString(locale, { day: "numeric", month: "long" }),
        }));
        return;
      }
      toast.error(t("profile.favoriteClubUpdateFailed"), {
        description: error instanceof Error ? error.message : t("profile.pleaseTryAgain"),
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleLanguageChange = async (language: string) => {
    if (isUpdating) return;
    if (!LOCALES.some((l) => l.code === language)) {
      toast.error(t("profile.invalidLanguage"));
      return;
    }
    setIsUpdating(true);
    try {
      const updated = await updateMe({ preferred_language: language });
      if (authUser) {
        setAuthenticated({ ...authUser, preferred_language: updated.preferred_language });
      }
      setLocale(language as Locale);
      toast.success(t("profile.languageUpdated"));
    } catch (error) {
      toast.error(t("profile.languageUpdateFailed"), {
        description: error instanceof Error ? error.message : t("profile.pleaseTryAgain"),
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <ProfileScreen
      player={{
        ...player,
        level: authUser?.progression?.level ?? player.level,
        xp: authUser?.progression?.currentLevelXp ?? player.xp,
        xpToNextLevel: authUser?.progression?.xpForNextLevel ?? player.xpToNextLevel,
        achievements,
        badges: [],
      }}
      avatarUrl={authUser?.avatar_url ?? null}
      country={authUser?.country ?? null}
      favoriteClub={authUser?.favorite_club ?? null}
      preferredLanguage={authUser?.preferred_language ?? null}
      globalRank={userRanks?.globalRank ?? null}
      countryRank={userRanks?.countryRank ?? null}
      matchStatsSummary={matchStatsSummary}
      rankedProfile={rankedProfile ?? null}
      rankedProfileLoading={rankedProfileLoading}
      recentMatches={recentMatches.map(toProfileRecentMatch)}
      recentMatchesLoading={recentMatchesLoading}
      recentMatchesError={
        recentMatchesError instanceof Error && recentMatches.length === 0
          ? recentMatchesError.message
          : null
      }
      previousNicknames={ownPublicProfile?.previousNicknames ?? []}
      nicknameChangesRemaining={authUser?.nickname_changes_remaining}
      nicknameChangesTotal={authUser?.nickname_changes_total}
      nicknameNextChangeAt={authUser?.nickname_next_change_at ?? null}
      onNameChange={handleNameChange}
      onAvatarChange={handleAvatarChange}
      onClubChange={handleClubChange}
      onLanguageChange={handleLanguageChange}
      isUpdating={isUpdating}
    />
  );
}

/** The 30-day club lock's end date, when the server refused a club switch for it. */
function clubCooldownRetryAt(error: unknown): Date | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const details = (error.data as { details?: { reason?: string; retryAt?: string } } | null)?.details;
  if (details?.reason !== "affiliation_cooldown" || !details.retryAt) return null;
  const date = new Date(details.retryAt);
  return Number.isNaN(date.getTime()) ? null : date;
}
